import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper
} from '@mui/material';
import { Add as AddIcon, ArrowBack as BackIcon, Forum as ForumIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const GroupDiscussions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('training_add');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [postBody, setPostBody] = useState('');
  const [categories, setCategories] = useState([]);
  const [spots, setSpots] = useState([]);
  const [titles, setTitles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', category_id: '', learning_spot_id: '', training_title_id: '' });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/group-discussions');
      setList(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      const r = await api.get(`/training/group-discussions/${id}`);
      setDetail(r.data.data || r.data);
    } catch (e) {
      toast.error('Discussion not found');
      navigate('/training/group-discussions');
    }
  }, [id, navigate]);

  useEffect(() => {
    api.get('/training/categories').then((r) => setCategories(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.category_id) {
      setSpots([]);
      setTitles([]);
      return;
    }
    api.get('/training/learning-spots', { params: { category_id: form.category_id } }).then((r) => setSpots(r.data.data || []));
  }, [form.category_id]);

  useEffect(() => {
    if (!form.learning_spot_id) {
      setTitles([]);
      return;
    }
    api.get(`/training/learning-spots/${form.learning_spot_id}/titles`).then((r) => setTitles(r.data.data || []));
  }, [form.learning_spot_id]);

  useEffect(() => {
    loadList();
  }, [loadList]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const createTopic = async () => {
    if (!form.title?.trim()) {
      toast.error('Title required');
      return;
    }
    try {
      const r = await api.post('/training/group-discussions', {
        title: form.title.trim(),
        body: form.body || null,
        category_id: form.category_id || null,
        learning_spot_id: form.learning_spot_id || null,
        training_title_id: form.training_title_id || null
      });
      const row = r.data.data || r.data;
      toast.success('Topic created');
      setDialogOpen(false);
      setForm({ title: '', body: '', category_id: '', learning_spot_id: '', training_title_id: '' });
      navigate(`/training/group-discussions/${row.id}`);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const sendPost = async () => {
    if (!postBody.trim() || !id) return;
    try {
      await api.post(`/training/group-discussions/${id}/posts`, { body: postBody.trim() });
      setPostBody('');
      loadDetail();
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to post');
    }
  };

  if (id && !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (id && detail) {
    return (
      <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/training/group-discussions')} sx={{ mb: 2 }}>
          All discussions
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {detail.title}
        </Typography>
        {detail.body && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {detail.body}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {detail.category && <Typography variant="caption">Category: {detail.category.name}</Typography>}
          {detail.learningSpot && <Typography variant="caption">Sub: {detail.learningSpot.name}</Typography>}
          {detail.course && <Typography variant="caption">Course: {detail.course.name}</Typography>}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Discussion
        </Typography>
        <Stack spacing={2}>
          {(detail.posts || []).map((p) => (
            <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {(p.author?.first_name || '') + ' ' + (p.author?.last_name || '')} ·{' '}
                {(p.createdAt || p.created_at) ? new Date(p.createdAt || p.created_at).toLocaleString() : ''}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {p.body}
              </Typography>
            </Paper>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Your message"
            value={postBody}
            onChange={(e) => setPostBody(e.target.value)}
          />
          <Button variant="contained" onClick={sendPost} sx={{ alignSelf: { sm: 'flex-start' } }}>
            Post
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/training/catalog')} sx={{ mb: 2 }}>
        Catalog
      </Button>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ForumIcon color="primary" />
          Group discussions
        </Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            New topic
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Topics can be scoped to a training category, sub-category, or course so the right cohort sees them.
      </Typography>

      {loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <Stack spacing={1.5}>
          {list.map((d) => (
            <Card key={d.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/training/group-discussions/${d.id}`)}>
              <CardContent>
                <Typography fontWeight={600}>{d.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[d.category?.name, d.learningSpot?.name, d.course?.name].filter(Boolean).join(' · ') || 'General'}
                </Typography>
              </CardContent>
            </Card>
          ))}
          {list.length === 0 && <Typography color="text.secondary">No topics yet.</Typography>}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New discussion topic</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <TextField
            fullWidth
            margin="normal"
            label="Description (optional)"
            multiline
            minRows={2}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Category (optional)</InputLabel>
            <Select
              label="Category (optional)"
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value, learning_spot_id: '', training_title_id: '' })}
            >
              <MenuItem value="">—</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" disabled={!form.category_id}>
            <InputLabel>Sub-category (optional)</InputLabel>
            <Select
              label="Sub-category (optional)"
              value={form.learning_spot_id}
              onChange={(e) => setForm({ ...form, learning_spot_id: e.target.value, training_title_id: '' })}
            >
              <MenuItem value="">—</MenuItem>
              {spots.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" disabled={!form.learning_spot_id}>
            <InputLabel>Course (optional)</InputLabel>
            <Select
              label="Course (optional)"
              value={form.training_title_id}
              onChange={(e) => setForm({ ...form, training_title_id: e.target.value })}
            >
              <MenuItem value="">—</MenuItem>
              {titles.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createTopic}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDiscussions;
